import { useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { Badge } from "./ui/badge";
import { Separator } from "./ui/separator";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { 
  LayoutDashboard, 
  Users, 
  Calendar, 
  Settings, 
  LogOut,
  UserCircle,
  ChevronRight,
  FileText,
  ListTodo,
  BarChart3,
  ClipboardList,
  Bell,
  Activity,
  KeyRound,
  Wallet,
  Contact,
  FileSignature,
  PartyPopper,
  Briefcase
} from "lucide-react";
import { toast } from "sonner";
import { apiFetch } from "../utils/api";
import { puoVedereAreaCommerciale } from "../utils/areaCommerciale";

const NAV_ITEMS = [
  { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { path: "/tasks", label: "Controllo di Gestione", icon: ListTodo },
  { path: "/notifiche", label: "Notifiche", icon: Bell },
  { path: "/clients", label: "Clienti", icon: Users },
  { path: "/scheda-cliente", label: "Scheda Cliente", icon: ClipboardList },
  { path: "/calendar", label: "Calendario", icon: Calendar },
  { path: "/social", label: "Social", icon: Activity },
  { path: "/crm", label: "CRM", icon: Contact },
  { path: "/contratti", label: "Contratti", icon: FileSignature },
  { path: "/contratti-eventi", label: "Contratti eventi", icon: PartyPopper },
];

// Solo per commerciali e direzione: stesse regole di GET /area-commerciale.
const AREA_COMMERCIALE_ITEM = { path: "/area-commerciale", label: "Area commerciale", icon: Briefcase };

const PM_ITEMS = [
  { path: "/stats", label: "Statistiche", icon: BarChart3 },
  { path: "/reports", label: "Report", icon: FileText },
  { path: "/team", label: "Team", icon: UserCircle },
  { path: "/settings", label: "Impostazioni", icon: Settings },
];

// Items for AMM role (administrative)
const AMM_ITEMS = [
  { path: "/stats", label: "Statistiche", icon: BarChart3 },
  { path: "/reports", label: "Report", icon: FileText },
  { path: "/compensi", label: "Compensi", icon: Wallet },
  { path: "/team", label: "Team", icon: UserCircle },
  { path: "/settings", label: "Impostazioni", icon: Settings },
];

export default function Sidebar({ user, setUser }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [unreadCount, setUnreadCount] = useState(0);
  // Rosso solo per ciò che è in ritardo o è un alert: il resto è accento, così
  // il rosso torna a voler dire qualcosa (vedi /notifications/unread-count).
  const [urgente, setUrgente] = useState(false);

  const isDirettore = user?.role === "direttore_tecnico" || user?.role === "direttore_commerciale" || !!user?.is_admin;
  const isPM = user?.role === "pm" || isDirettore || (user?.secondary_roles || []).includes("pm") || !!user?.is_admin;
  const isAMM = user?.role === "amm" || isDirettore || (user?.secondary_roles || []).includes("amm") || !!user?.is_admin;

  // Polling notifiche ogni 60s
  useEffect(() => {
    const fetchCount = async () => {
      try {
        const res = await apiFetch("/notifications/unread-count");
        if (res.ok) {
          const data = await res.json();
          setUnreadCount(data.total || 0);
          setUrgente(!!data.urgente);
        }
      } catch (e) {/* ignore */}
    };
    fetchCount();
    const id = setInterval(fetchCount, 60000);
    // La pagina Notifiche lo emette dopo ogni lettura o chiusura.
    window.addEventListener("notifiche-aggiornate", fetchCount);
    return () => {
      clearInterval(id);
      window.removeEventListener("notifiche-aggiornate", fetchCount);
    };
  }, [location.pathname]);

  const handleLogout = async () => {
    try {
      await apiFetch("/auth/logout", { method: "POST" });
      // Clear token from localStorage
      localStorage.removeItem("session_token");
      setUser(null);
      navigate("/login", { replace: true });
      toast.success("Logout effettuato");
    } catch (error) {
      toast.error("Errore durante il logout");
    }
  };

  // Change password dialog state
  const [pwdOpen, setPwdOpen] = useState(false);
  const [currentPwd, setCurrentPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [pwdSaving, setPwdSaving] = useState(false);

  const handleChangePassword = async () => {
    if (newPwd.length < 6) {
      toast.error("La nuova password deve essere di almeno 6 caratteri");
      return;
    }
    if (newPwd !== confirmPwd) {
      toast.error("Le password non corrispondono");
      return;
    }
    setPwdSaving(true);
    try {
      const res = await apiFetch("/auth/change-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          current_password: currentPwd || undefined,
          new_password: newPwd,
        }),
      });
      if (res.ok) {
        toast.success("Password aggiornata");
        setPwdOpen(false);
        setCurrentPwd("");
        setNewPwd("");
        setConfirmPwd("");
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Errore aggiornamento password");
      }
    } catch (e) {
      toast.error("Errore di rete");
    } finally {
      setPwdSaving(false);
    }
  };

  const isActive = (path) => location.pathname === path;
  // L'Area commerciale subito dopo la Dashboard: per un commerciale è la home.
  const navItems = puoVedereAreaCommerciale(user)
    ? [NAV_ITEMS[0], AREA_COMMERCIALE_ITEM, ...NAV_ITEMS.slice(1)]
    : NAV_ITEMS;

  return (
    <aside className="liquid-glass fixed inset-y-3 left-3 w-[14.5rem] rounded-[24px] flex flex-col overflow-hidden z-40">
      {/* Logo */}
      <div className="p-6 flex items-center gap-3">
        <img src="/diecixcento-mark.png" alt="Diecixcento" className="w-9 h-9 rounded-lg object-contain bg-black p-1" />
        <div className="flex-1 min-w-0">
          <img src="/diecixcento-logo.png" alt="diecixcento" className="h-7 w-auto object-contain object-left" />
          <p className="text-[10px] text-muted-foreground mt-1 tracking-wider uppercase">Dashboard app</p>
        </div>
      </div>

      <Separator />

      {/* Navigation */}
      <nav className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-4 space-y-1 [scrollbar-gutter:stable]">
        {navItems.map((item) => (
          <Button
            key={item.path}
            variant={isActive(item.path) ? "secondary" : "ghost"}
            className={`group w-full justify-start transition-all duration-200 hover:translate-x-1 hover:bg-indigo-50/70 hover:text-indigo-700 ${isActive(item.path) ? "bg-indigo-50 text-indigo-700" : ""}`}
            onClick={() => navigate(item.path)}
            data-testid={`nav-${item.path.slice(1)}`}
          >
            <item.icon className="w-4 h-4 mr-3 transition-transform duration-200 group-hover:scale-110 group-hover:-rotate-3" />
            {item.label}
            {item.path === "/notifiche" && unreadCount > 0 && (
              <Badge className={`ml-auto text-white ${urgente ? "bg-[#c0392b] hover:bg-[#a93226]" : "bg-[#5b5bd6] hover:bg-[#4a4ac4]"}`} data-testid="nav-notif-badge">
                {unreadCount > 99 ? "99+" : unreadCount}
              </Badge>
            )}
            {isActive(item.path) && item.path !== "/notifiche" && <ChevronRight className="w-4 h-4 ml-auto" />}
          </Button>
        ))}

        {isPM && (
          <>
            <Separator className="my-4" />
            <p className="text-xs text-muted-foreground uppercase tracking-wider px-3 mb-2">
              Gestione
            </p>
            {PM_ITEMS.map((item) => (
              <Button
                key={item.path}
                variant={isActive(item.path) ? "secondary" : "ghost"}
                className={`group w-full justify-start transition-all duration-200 hover:translate-x-1 hover:bg-indigo-50/70 hover:text-indigo-700 ${isActive(item.path) ? "bg-indigo-50 text-indigo-700" : ""}`}
                onClick={() => navigate(item.path)}
                data-testid={`nav-${item.path.slice(1)}`}
              >
                <item.icon className="w-4 h-4 mr-3 transition-transform duration-200 group-hover:scale-110 group-hover:-rotate-3" />
                {item.label}
                {isActive(item.path) && <ChevronRight className="w-4 h-4 ml-auto" />}
              </Button>
            ))}
          </>
        )}

        {isAMM && (
          <>
            <Separator className="my-4" />
            <p className="text-xs text-muted-foreground uppercase tracking-wider px-3 mb-2">
              Amministrazione
            </p>
            {AMM_ITEMS.map((item) => (
              <Button
                key={item.path}
                variant={isActive(item.path) ? "secondary" : "ghost"}
                className={`group w-full justify-start transition-all duration-200 hover:translate-x-1 hover:bg-indigo-50/70 hover:text-indigo-700 ${isActive(item.path) ? "bg-indigo-50 text-indigo-700" : ""}`}
                onClick={() => navigate(item.path)}
                data-testid={`nav-amm-${item.path.slice(1)}`}
              >
                <item.icon className="w-4 h-4 mr-3 transition-transform duration-200 group-hover:scale-110 group-hover:-rotate-3" />
                {item.label}
                {isActive(item.path) && <ChevronRight className="w-4 h-4 ml-auto" />}
              </Button>
            ))}
          </>
        )}
      </nav>

      {/* User Section */}
      <div className="shrink-0 bg-transparent p-4 border-t border-white/50">
        <div className="flex items-center gap-3 mb-3">
          <Avatar className="h-10 w-10">
            <AvatarImage src={user?.picture} alt={user?.name} />
            <AvatarFallback className="bg-indigo-100 text-indigo-700">
              {user?.name?.charAt(0) || "?"}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-foreground truncate">
              {user?.name}
            </p>
            <Badge variant="secondary" className="text-xs mt-0.5">
              {user?.is_admin && user?.email?.toLowerCase() === "info@diecixcento.com" ? "Admin" :
               user?.role === "direttore_tecnico" ? "Direttore Tecnico" : 
               user?.role === "direttore_commerciale" ? "Direttore Commerciale" :
               user?.role === "amm" ? "Amministrazione" :
               user?.role === "pm" ? "Project Manager" :
               user?.role === "videomaker" ? "Content Creator" :
               user?.role === "editor" ? "Editor" :
               user?.role === "filmmaker" ? "Videomaker" :
               user?.role === "commerciale" ? "Commerciale" : "SMM"}
            </Badge>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="group w-full justify-start text-muted-foreground transition-colors duration-200 hover:bg-indigo-50 hover:text-indigo-700 mb-2"
          onClick={() => setPwdOpen(true)}
          data-testid="change-password-btn"
        >
          <KeyRound className="w-4 h-4 mr-3 transition-transform duration-200 group-hover:rotate-12 group-hover:scale-110" />
          Cambia password
        </Button>
        <Button
          variant="outline"
          className="group w-full justify-start text-muted-foreground transition-colors duration-200 hover:border-red-200 hover:bg-red-50 hover:text-red-600"
          onClick={handleLogout}
          data-testid="logout-btn"
        >
          <LogOut className="w-4 h-4 mr-3 transition-transform duration-200 group-hover:translate-x-1" />
          Esci
        </Button>
      </div>

      {/* Change Password Dialog */}
      <Dialog open={pwdOpen} onOpenChange={setPwdOpen}>
        <DialogContent className="sm:max-w-md" data-testid="change-password-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <KeyRound className="w-5 h-5" />
              Cambia Password
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="current-pwd">Password attuale (lascia vuoto se non ne hai mai impostata una)</Label>
              <Input
                id="current-pwd"
                type="password"
                value={currentPwd}
                onChange={(e) => setCurrentPwd(e.target.value)}
                data-testid="current-password-input"
                autoComplete="current-password"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="new-pwd">Nuova password (min 6 caratteri)</Label>
              <Input
                id="new-pwd"
                type="password"
                value={newPwd}
                onChange={(e) => setNewPwd(e.target.value)}
                data-testid="new-password-input"
                autoComplete="new-password"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="confirm-pwd">Conferma nuova password</Label>
              <Input
                id="confirm-pwd"
                type="password"
                value={confirmPwd}
                onChange={(e) => setConfirmPwd(e.target.value)}
                data-testid="confirm-password-input"
                autoComplete="new-password"
              />
            </div>
            <Button
              onClick={handleChangePassword}
              disabled={pwdSaving}
              className="w-full bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="confirm-change-password-btn"
            >
              {pwdSaving ? "Salvataggio..." : "Aggiorna password"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </aside>
  );
}
